import { APIComponentType, ArdoqId } from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
// TODO makes probably more sense to move this to data-model,
// `getDefaultCSSColor` assigns a hardcoded color to a component type
// based on the type hierarchy depth.
import {
  ensureContrast,
  getDefaultCSSColor,
  getLightenedColor,
  getShadedColor,
} from '@ardoq/color-helpers';
// TODO makes probably more sense to move this to data-model. It return an
// ArdoqIcon which is a pure data structure without any visual data, dealing
// only with icon names (or ids).
import { ArdoqIcon, getIcon } from '@ardoq/icons';
import type { EnhancedScopeData, RepresentationData } from '@ardoq/data-model';
import { colors } from '@ardoq/design-tokens';
import { ComponentRepresentationDataWithColorShades } from '@ardoq/basic-components';

export const getComponentRepresentationData = (
  enhancedScopeData: EnhancedScopeData,
  id: string
): RepresentationData & { icon: ArdoqIcon; value: string } => {
  const component = enhancedScopeData.componentsById[id];
  if (!component) {
    logError(Error('missing component in enhancedScopeData.componentsById'));
    return getFallbackComponentRepresentation();
  }

  if (component.image) {
    return {
      icon: getIcon(null),
      isImage: true,
      value: component.image,
    };
  }

  if (component.icon) {
    return {
      icon: getIcon(component.icon),
      isImage: false,
      value: component.icon,
    };
  }

  const { typeId, model } = component;
  return typeId && model
    ? getComponentTypeRepresentationData(enhancedScopeData, typeId, model)
    : getFallbackComponentRepresentation();
};

export const getComponentTypeRepresentationData = (
  enhancedScopeData: EnhancedScopeData,
  entityId: ArdoqId,
  parentEntityId: ArdoqId | undefined
) => {
  const type = parentEntityId
    ? enhancedScopeData.typesByModelId[parentEntityId]?.componentTypesById[
        entityId
      ]
    : undefined;

  const value = type?.image || type?.icon || '';

  return type
    ? {
        value,
        isImage: Boolean(type.image),
        icon: getIcon(value),
      }
    : getFallbackComponentRepresentation();
};

export const DEFAULT_COLOR = colors.brand800;

export const getComponentColor = (
  enhancedScopeData: EnhancedScopeData,
  id: string,
  type: APIComponentType
) => {
  const component = enhancedScopeData.componentsById[id];
  if (!component) {
    logError(Error('missing component in enhancedScopeData.componentsById'));
    return DEFAULT_COLOR;
  }

  if (component.color) {
    return component.color;
  }

  if (type?.color) {
    return type.color;
  }

  return type ? getDefaultCSSColor(type.level) : DEFAULT_COLOR;
};

export const getComponentTypeColor = (
  enhancedScopeData: EnhancedScopeData,
  id: string,
  parentId: string
) => {
  const type =
    enhancedScopeData.typesByModelId[parentId]?.componentTypesById[id];

  if (!type) {
    logError(
      Error(
        'missing component in enhancedScopeData.typesByModelId[<ModelId>].componentTypesById[<ComponentTypeId>]'
      )
    );
    return DEFAULT_COLOR;
  }

  return type.color || getDefaultCSSColor(type.level);
};

export const getFallbackComponentRepresentation = () => ({
  icon: getIcon(null),
  isImage: false,
  value: '',
});

export const getComponentTypeRepresentationDataWithColorShades = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId,
  typeId: string
): ComponentRepresentationDataWithColorShades => {
  const color = getComponentTypeColor(scopeData, typeId, modelId);
  const shadedColor = getShadedColor(color);
  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);

  const { icon, value, isImage } =
    getComponentTypeRepresentationData(scopeData, typeId, modelId) ??
    getFallbackComponentRepresentation();

  return {
    icon,
    value,
    isImage,
    color,
    shadedColor,
    lightenedColor,
    contrastColor,
  };
};

export const getComponentRepresentationDataWithColorShades = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): ComponentRepresentationDataWithColorShades => {
  const component = scopeData.componentsById[componentId];

  if (!component) {
    logError(Error('missing component in enhancedScopeData.componentsById'));
    return getComponentRepresentationDataWithColorShadesFallback();
  }

  const componentType =
    scopeData.typesByModelId[component.model]?.componentTypesById[
      component.typeId
    ];

  const color = getComponentColor(scopeData, componentId, componentType);
  const shadedColor = getShadedColor(color);
  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, shadedColor);

  const { icon, value, isImage } = getComponentRepresentationData(
    scopeData,
    componentId
  );

  return {
    icon,
    value,
    isImage,
    color,
    shadedColor,
    lightenedColor,
    contrastColor,
  };
};

export const getComponentRepresentationDataWithColorShadesFallback = () => {
  const fallback = getFallbackComponentRepresentation();
  return {
    ...fallback,
    color: DEFAULT_COLOR,
    shadedColor: getShadedColor(DEFAULT_COLOR),
    lightenedColor: getLightenedColor(DEFAULT_COLOR),
    contrastColor: ensureContrast(
      getLightenedColor(DEFAULT_COLOR),
      getShadedColor(DEFAULT_COLOR)
    ),
  };
};
